/* Dependencies */
import React, { useEffect, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* Custom hooks */
import useOrganization from 'hooks/UseOrganization';

/* Constants */
import { KEYS_LOCALSTORAGE } from 'constants/general';
import { APP_SECTIONS_PATHS } from 'constants/routes';

/* Styled componentes */
import Text from './styles/TopBarText';
import Container from './styles/TopBarItemContainer';
import TopBarContainer from './styles/TopBarContainer';

/* Images */
import Icons from '../../images/icons';
import emptyPhoto from '../../images/icons/emptyPhoto.svg';
import organizationIcon from '../../images/organization.png';
import useCenters from '../../hooks/centers/UseCenters';
import useParams from 'hooks/UseParams';
import { isRoleType, isTeacher } from 'lib/helpers';
import { ROLES_OBJECT } from 'constants/onBoarding';
import { AppContext } from 'App';
import HelpButton from 'components/appcues/HelpButton';
import i18n from 'i18n';

const { BackArrow, MaterialsIcon, HamburgerMenu } = Icons;

/* Interfaces */
interface ITopBarProps {
  history: any;
  toggleMenu: Function;
}

enum Language {
  ES = 'es',
  EN = 'en',
  PT = 'pt',
};

const TopBar: React.FC<ITopBarProps> = ({ toggleMenu, history }) => {
  const [t] = useTranslation();
  const { user } = useContext(AppContext);
  const { new_announcement: newAnnouncement } = useParams();
  const { organization } = useOrganization();
  const { activeCenter } = useCenters();
  const [language, setLanguage] = useState<string>(Language.ES);

  const orgId = organization?.id;
  const orgIdfromCenter = activeCenter?.organization_id;

  localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_ID, orgId);
  const translation = t;

  const orgName = organization?.name || localStorage[KEYS_LOCALSTORAGE.ORGANIZATION_NAME];
  const schoolName = isTeacher() ? activeCenter?.name : orgName;


  const onLanguageChanged = (lang: string) => {
    setLanguage(lang);
  };

  const shouldDisplayHelperButton = useMemo(() => language === Language.ES, [language]);

  // TODO: Create a context that allow us to listen this change and make it reactive along the components.
  useEffect(() => {
    i18n.on('languageChanged', onLanguageChanged);

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    if (orgId && !isRoleType([ROLES_OBJECT.TEACHER.value])) {
      localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_ID, orgId);
      localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_GLOBAL_ID, orgId);
    }
  }, [orgId]);

  useMemo(() => {
    if (orgIdfromCenter && isRoleType([ROLES_OBJECT.TEACHER.value])) {
      localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_GLOBAL_ID, orgIdfromCenter);
      localStorage.setItem(KEYS_LOCALSTORAGE.ORGANIZATION_ID, orgIdfromCenter);
    }
  }, [orgIdfromCenter]);

  function defaulMobileHeader() {
    return (
      <>
        <HamburgerMenu className="hide-t" onClick={() => toggleMenu()} />
        <img
          alt="emptyPhoto"
          onClick={() => history.push(APP_SECTIONS_PATHS.USER_PROFILE)}
          className="hide-t"
          src={emptyPhoto}
          width={40}
          height={40}
        />
      </>
    );
  }

  const renderMobileTopBar = () => {
    switch (history.location.pathname) {
    case APP_SECTIONS_PATHS.MESSAGES: {
      if (newAnnouncement) {
        return (
          <>
            <BackArrow
              className="hide-t"
              onClick={() => history.push(APP_SECTIONS_PATHS.MESSAGES)}
            />
            <Text className="hide-t">{translation('common:MENU.COMMUNITY')}</Text>
            <Container className="hide-t" />
          </>
        );
      }
      return (
        <>
          <HamburgerMenu className="hide-t" onClick={() => toggleMenu()} />
          <Text className="hide-t">{translation('common:MENU.COMMUNITY')}</Text>
          <Container className="hide-t" />
        </>
      );
    }
    case APP_SECTIONS_PATHS.ADD_STUDENTS:
      return (
        <>
          <BackArrow className="hide-t" onClick={() => history.goBack()} />
          <Text className="hide-t">{translation('dap:STUDENTS.ADD')}</Text>
          <Container className="hide-t" />
        </>
      );

    case APP_SECTIONS_PATHS.USER_PROFILE:
      return (
        <BackArrow className="hide-t" onClick={() => history.goBack()} />
      );

    case APP_SECTIONS_PATHS.SETTINGS:
      return (
        <>
          <HamburgerMenu className="hide-t" onClick={() => toggleMenu()} />
          <Text className="hide-t">{translation('settings:SETTINGS')}</Text>
          <Container className="hide-t" />
        </>
      );

    default:
      return defaulMobileHeader();
    }
  };

  return (
    <TopBarContainer>
      {renderMobileTopBar()}
      <Container className="hide-p show-lt">
        <img
          id="TopBarOrganizationIcon"
          alt="organizationIcon"
          src={organization?.logo ? organization.logo : organizationIcon}
        />
        <Text>{schoolName}</Text>
      </Container>
      <Container className="hide-p show-t">
        {shouldDisplayHelperButton && false  ? <HelpButton lang={language} /> : null}
        <Container
          className="hide-p show-t"
          id="TopBarUserProfileSection"
        >
          <img
            id="TopBarUserAvatar"
            alt="emptyPhoto"
            src={user?.avatar ? user.avatar : emptyPhoto}
          />
          <Text>
            {user.name}
            {' '}
            {user.lastname}
            {' '}
          </Text>
          <MaterialsIcon
            id="TopBarMaterialsIcon"
            onClick={() => history.push(APP_SECTIONS_PATHS.USER_PROFILE)}
          />
        </Container>
      </Container>
    </TopBarContainer>
  );
};

export default TopBar;
